import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  FiPhone,
  FiClock,
  FiZap,
  FiUsers,
  FiRefreshCw,
} from "react-icons/fi";
import {
  getVoiceAgentsDF,
  getCallHistoryDF,
} from "../../../services/voiceDataFabricService";
import "../../../styles/Voice.css";
import "../../../styles/DashboardWidgets.css";

interface RawCall {
  direction: "in" | "out" | "missed";
  durationSecs: number;
  created: string | null;
}

const axisStyle = {
  fontSize: 9,
  fill: "#94a3b8",
  fontFamily: "Poppins, Manrope, sans-serif",
};

const gridStroke = "rgba(148,163,184,0.12)";

const SidebarTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "rgba(15,10,30,0.92)",
        border: "1px solid rgba(139,92,246,0.25)",
        borderRadius: 6,
        padding: "6px 10px",
        fontSize: 11,
      }}
    >
      {label && (
        <p style={{ color: "#94a3b8", marginBottom: 2, fontSize: 10 }}>
          {label}
        </p>
      )}
      {payload.map((e: any, i: number) => (
        <div
          key={i}
          style={{ display: "flex", alignItems: "center", gap: 5 }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: e.color || e.fill,
              flexShrink: 0,
            }}
          />
          <span style={{ color: "#e2e8f0" }}>{e.value}</span>
        </div>
      ))}
    </div>
  );
};

const VoiceReportsSidebar: React.FC = () => {
  const [rawCalls, setRawCalls] = useState<RawCall[]>([]);
  const [agentCount, setAgentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dfAgents, dfCalls] = await Promise.all([
        getVoiceAgentsDF(),
        getCallHistoryDF(),
      ]);
      setAgentCount(dfAgents.length);
      setRawCalls(
        dfCalls.map((c) => {
          const d = (c.direction ?? "").toLowerCase();
          const secs =
            c.callDurationSecs != null ? parseInt(c.callDurationSecs, 10) : 0;
          return {
            direction: d.includes("inbound")
              ? "in"
              : d.includes("outbound")
                ? "out"
                : "missed",
            durationSecs: isNaN(secs) ? 0 : secs,
            created: c.created,
          };
        }),
      );
    } catch (e: any) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stats = useMemo(() => {
    const total = rawCalls.length;
    const totalSecs = rawCalls.reduce((s, c) => s + c.durationSecs, 0);
    const inbound = rawCalls.filter((c) => c.direction === "in").length;
    const outbound = rawCalls.filter((c) => c.direction === "out").length;
    return {
      calls: total,
      avgSecs: total ? Math.round(totalSecs / total) : 0,
      minutes: Math.round(totalSecs / 60),
      inbound,
      outbound,
    };
  }, [rawCalls]);

  const weeklyTrend = useMemo(() => {
    const buckets: Record<string, { key: number; label: string; Calls: number }> = {};
    rawCalls.forEach((c) => {
      if (!c.created) return;
      const d = new Date(c.created);
      if (isNaN(d.getTime())) return;
      const label =
        d.toLocaleString("en-US", { month: "short" }) +
        " W" +
        (Math.floor((d.getDate() - 1) / 7) + 1);
      const key =
        d.getFullYear() * 10000 +
        (d.getMonth() + 1) * 100 +
        Math.floor((d.getDate() - 1) / 7);
      if (!buckets[label]) buckets[label] = { key, label, Calls: 0 };
      buckets[label].Calls += 1;
    });
    return Object.values(buckets)
      .sort((a, b) => a.key - b.key)
      .map(({ label, Calls }) => ({ label, Calls }));
  }, [rawCalls]);

  const durationDist = useMemo(() => {
    const b = { "<30s": 0, "30s-1m": 0, "1m-2m": 0, "2m-5m": 0, "5m+": 0 };
    rawCalls.forEach((c) => {
      const s = c.durationSecs;
      if (s < 30) b["<30s"]++;
      else if (s < 60) b["30s-1m"]++;
      else if (s < 120) b["1m-2m"]++;
      else if (s < 300) b["2m-5m"]++;
      else b["5m+"]++;
    });
    return Object.entries(b).map(([label, Calls]) => ({ label, Calls }));
  }, [rawCalls]);

  if (loading) {
    return (
      <div className="vrs__root cc-sb-dashboard">
        <div className="vrs__stats-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="vrs__stat-card vrs__stat-card--skel">
              <div className="vrs__skel" style={{ height: 9, width: "60%", marginBottom: 8 }} />
              <div className="vrs__skel" style={{ height: 22, width: "45%" }} />
            </div>
          ))}
        </div>
        <div className="vrs__chart-skel" />
        <div className="vrs__chart-skel" />
        <div className="vrs__chart-skel" style={{ height: 100 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="vrs__root cc-sb-dashboard vrs__error">
        <FiRefreshCw size={18} style={{ color: "#ef4444", marginBottom: 8 }} />
        <span style={{ fontSize: 12, color: "#94a3b8" }}>{error}</span>
        <button
          className="glass-btn"
          style={{ fontSize: 11, marginTop: 10 }}
          onClick={loadData}
        >
          <FiRefreshCw size={11} /> Retry
        </button>
      </div>
    );
  }

  const statCards = [
    { cls: "cc-sb-stat--green",  lbl: "TOTAL CALLS",    ctx: "Inbound + outbound",      val: stats.calls,           numCls: "cc-sb-stat__num--green"  },
    { cls: "cc-sb-stat--amber",  lbl: "AVG DURATION",   ctx: "Per call average",         val: `${stats.avgSecs}s`,   numCls: "cc-sb-stat__num--amber"  },
    { cls: "cc-sb-stat--purple",  lbl: "TOTAL MINUTES",  ctx: "Combined talk time",       val: stats.minutes,          numCls: "cc-sb-stat__num--purple"  },
    { cls: "cc-sb-stat--green",  lbl: "INBOUND",        ctx: "Calls received",           val: stats.inbound,          numCls: "cc-sb-stat__num--green"  },
    { cls: "cc-sb-stat--amber", lbl: "OUTBOUND",       ctx: "Calls made",               val: stats.outbound,         numCls: "cc-sb-stat__num--amber" },
    { cls: "cc-sb-stat--purple", lbl: "AI AGENTS",      ctx: "Active voice agents",      val: agentCount,             numCls: "cc-sb-stat__num--purple" },
  ];

  return (
    <div className="vrs__root cc-sb-dashboard">
      {/* Refresh row */}
      {/* <div className="vrs__refresh-row">
        <button
          className="glass-btn vrs__refresh-btn"
          onClick={loadData}
          title="Refresh"
        >
          <FiRefreshCw size={11} /> Refresh
        </button>
      </div> */}

      {/* Stat cards */}
      <div className="cc-sb-stats">
        {statCards.map((s, i) => (
          <div key={i} className={`cc-sb-stat ${s.cls}`}>
            <div className="cc-sb-stat__body">
              <span className="cc-sb-stat__label">{s.lbl}</span>
              <span className="cc-sb-stat__context">{s.ctx}</span>
            </div>
            <span className={`cc-sb-stat__num ${s.numCls}`}>{s.val}</span>
          </div>
        ))}
      </div>

      {/* Weekly Calls Trend */}
      {weeklyTrend.length > 0 && (
        <div className="vrs__chart-block cc-sb-split-card">
          <div className="vrs__chart-title">Weekly Calls Trend</div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart
              data={weeklyTrend}
              margin={{ top: 6, right: 4, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="vrsAreaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis dataKey="label" tick={axisStyle} axisLine={false} tickLine={false} />
              <YAxis tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<SidebarTooltip />} />
              <Area
                type="monotone"
                dataKey="Calls"
                stroke="#8b5cf6"
                strokeWidth={2}
                fill="url(#vrsAreaGrad)"
                dot={false}
                activeDot={{ r: 4, fill: "#8b5cf6" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Duration Distribution */}
      {durationDist.some((d) => d.Calls > 0) && (
        <div className="vrs__chart-block cc-sb-split-card">
          <div className="vrs__chart-title">Duration Distribution</div>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart
              data={durationDist}
              margin={{ top: 6, right: 4, left: -20, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis dataKey="label" tick={axisStyle} axisLine={false} tickLine={false} />
              <YAxis tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<SidebarTooltip />} />
              <Bar dataKey="Calls" fill="#22d3ee" radius={[3, 3, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Call mix breakdown */}
      {stats.calls > 0 && (
        <div className="vrs__chart-block cc-sb-split-card">
          <div className="vrs__chart-title">Call Mix</div>
          <div className="vrs__call-mix">
            {[
              { label: "Inbound",  value: stats.inbound,  color: "#38bdf8" },
              { label: "Outbound", value: stats.outbound, color: "#a78bfa" },
              { label: "Missed",   value: rawCalls.filter(c => c.direction === "missed").length, color: "#f87171" },
            ].map((m) => (
              <div key={m.label} className="vrs__mix-row">
                <span className="vrs__mix-dot" style={{ background: m.color }} />
                <span className="vrs__mix-label">{m.label}</span>
                <div className="vrs__mix-bar-bg">
                  <div
                    className="vrs__mix-bar-fill"
                    style={{
                      width: stats.calls ? `${(m.value / stats.calls) * 100}%` : "0%",
                      background: m.color,
                    }}
                  />
                </div>
                <span className="vrs__mix-count" style={{ color: m.color }}>
                  {m.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceReportsSidebar;
